import { cn } from '@/lib/utils'
import { DropdownMenuLabel } from '@radix-ui/react-dropdown-menu'
import type { FontAwesomeIcon } from '@ur-solutions/react-components'
import type { ReactNode } from 'react'
import type { LinkProps } from 'react-router'
import { Link } from 'react-router'
import type { FAIconProps } from './fa-icon'
import { FAIcon } from './fa-icon'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui'

interface TableMenuItemCommon {
  id: string
}

interface TableMenuItemRegular extends TableMenuItemCommon {
  type: 'regular'
  label: ReactNode
  icon?: FontAwesomeIcon | FAIconProps
  onClick(): void
}

interface TableMenuItemSeparator extends TableMenuItemCommon {
  type: 'separator'
}

interface TableMenuItemLabel extends TableMenuItemCommon {
  type: 'label'
  label: ReactNode
}

interface TableMenuLink extends TableMenuItemCommon {
  type: 'link'
  label: ReactNode
  icon?: FontAwesomeIcon | FAIconProps
  to: LinkProps['to']
  target?: LinkProps['target']
}

export type TableMenuItem =
  | TableMenuItemRegular
  | TableMenuItemSeparator
  | TableMenuItemLabel
  | TableMenuLink

interface TableMenuProps {
  items: TableMenuItem[]
}

export function TableMenu({ items }: TableMenuProps) {
  function getIconProps(
    icon: FontAwesomeIcon | FAIconProps | undefined
  ): FAIconProps | null {
    if (!icon) return null

    return typeof icon === 'object' ? icon : { icon }
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <div className="cursor-pointer" role="button">
          <FAIcon
            icon="ellipsis-v"
            className="text-stone-600 hover:text-secondary"
          />
        </div>
      </DropdownMenuTrigger>

      <DropdownMenuContent>
        {items.map(item => {
          switch (item.type) {
            case 'regular': {
              const iconProps = getIconProps(item.icon)

              return (
                <DropdownMenuItem key={item.id} onClick={item.onClick}>
                  {iconProps && (
                    <FAIcon
                      {...iconProps}
                      className={cn(iconProps.className, 'mr-2')}
                    />
                  )}

                  {item.label}
                </DropdownMenuItem>
              )
            }
            case 'separator':
              return <DropdownMenuSeparator key={item.id} />
            case 'label':
              return (
                <DropdownMenuLabel key={item.id}>
                  {item.label}
                </DropdownMenuLabel>
              )
            case 'link': {
              const iconProps = getIconProps(item.icon)

              return (
                <DropdownMenuItem key={item.id}>
                  <Link
                    className={cn(
                      'text-inherit hover:no-underline cursor-default'
                    )}
                    to={item.to}
                    target={item.target}
                  >
                    {iconProps && (
                      <FAIcon
                        {...iconProps}
                        className={cn(iconProps.className, 'mr-2')}
                      />
                    )}

                    {item.label}
                  </Link>
                </DropdownMenuItem>
              )
            }
          }
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
